import React, { useState, useMemo, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import BasicTabs from '../../../components/tabs/basic-tabs/basic-tabs.component'
import { verifyOrderTabs } from '../verify-order.constant'
import TabPanel from '../../../components/tabs/tab-panel/tab-panel.component'
import LoadCustomerForm from '../../../components/forms/load-customer-form/load-customer-form.component'
import LoadRateForm from '../../../components/forms/load-rate-form/load-rate-form.component'
import ActionButtons from '../../../components/action-buttons/action-buttons.component'
import LoadPickupForm from '../../../components/forms/load-pickup-form/load-pickup-form.component'
import LoadDeliveryForm from '../../../components/forms/load-delivery-form/load-delivery-form.component'
import LoadNotesForm from '../../../components/forms/load-notes-form/load-notes-form.component'
import { FormProvider } from 'react-hook-form'
import { useVerifyOrderForm } from './verify-order-form.hooks'
import { Order, OrderEvent } from '../../../store/orders/orders.types'
import { Company } from '../../../store/company/company.types'
import UpdateOrderConfirmationDialog from '../../../components/update-order-confirmation-dialog/update-order-confirmation-dialog.component'
import { orderMock } from '../../../store/orders/orders.fixtures'
import { formatPhoneForDisplay, formatPhoneForSave } from 'utils/phone'
import { getRole } from 'utils/common'
import { IconButton } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'

export interface VerifyOrderFormProps {
  order: Order
  company?: Company
  onClose?: () => void
}

const VerifyOrderForm = ({ order, company, onClose }: VerifyOrderFormProps) => {
  const navigate = useNavigate()
  const [tabsValue, setTabsValue] = useState(0)
  const [updateValue, setUpdateValue] = useState<Order>(orderMock)
  const [updateCompany, setUpdateCompany] = useState<Company | undefined>(company)
  const [alertOpen, setAlertOpen] = useState(false)
  // Track orderEvents with their orderEventId for managing new vs existing events
  const [orderEventsWithIds, setOrderEventsWithIds] = useState<Array<OrderEvent & { _formIndex?: number }>>([])

  const role = getRole()
  const isDispatch = role === 'DISPATCH'

  // Merge order and company data for form
  const orderForForm = useMemo(() => {
    // Build address from company data
    const address = company
      ? `${company.streetNumber || ''} ${company.streetName || ''} ${company.city || ''} ${company.state || ''}`.trim()
      : ''
    
    return {
      ...order,
      address: address,
      // Customer name from company details (company API); same as companyName in UI, different columns in DB
      customerName: company?.companyName ?? order.customerName ?? '',
      notificationEmail: company?.notificationEmail || '',
      billingEmail: company?.invoicePodEmail || '',
      accountPayableEmail: company?.accountPayableEmail || '',
      phone: formatPhoneForDisplay(company?.phoneNumber || ''),
    }
  }, [order, company])

  const formMethods = useVerifyOrderForm(orderForForm)

  // Reset form when order or company changes
  useEffect(() => {
    const address = company
      ? `${company.streetNumber || ''} ${company.streetName || ''} ${company.city || ''} ${company.state || ''}`.trim()
      : ''
    
    // Store orderEvents with their IDs for tracking
    const eventsWithIds = (order.orderEvents || []).map((event, index) => ({
      ...event,
      _formIndex: index,
    }))
    setOrderEventsWithIds(eventsWithIds)
    
    // Convert orderEvents to match form schema (numbers to strings)
    const formattedOrderEvents = eventsWithIds.map((event) => ({
      startTime: event.startTime || '',
      endTime: event.endTime || '',
      name: event.name || '',
      address: event.address || '',
      loadType: event.loadType || '',
      loadCount: event.loadCount?.toString() || '',
      temperatureValue: event.temperatureValue?.toString() || '',
      hazmat: event.hazmat || '',
      pickupNumber: event.pickupNumber || '',
      weightValue: event.weightValue?.toString() || '',
      notes: event.notes || '',
      _orderEventId: event.orderEventId?.toString() || '', // Store orderEventId for tracking
      _eventType: event.eventType || '', // Store eventType for tracking
    }))
    
    formMethods.reset({
      customerName: company?.companyName ?? order.customerName ?? '',
      address: address,
      loadNumber: order.loadNumber || '',
      billingEmail: company?.invoicePodEmail || '',
      notificationEmail: company?.notificationEmail || '',
      accountPayableEmail: company?.accountPayableEmail || '',
      phone: formatPhoneForDisplay(company?.phoneNumber || ''),
      other: '',
      currency: order.currency || 'CAD',
      baseRate: order.baseRate?.toString() || '',
      detentionCharges: order.detentionCharges?.toString() || '',
      layoverCharges: order.layoverCharges?.toString() || '',
      fuelSurcharge: order.fuelSurcharge?.toString() || '',
      otherCharges: order.otherCharges?.toString() || '',
      notes: order.notes || '',
      orderEvents: formattedOrderEvents,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order, company, formMethods.reset])

  const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabsValue(newValue)
  }

  // Calculate pickup and delivery indices and counts based on current form state
  const { pickupIndices, deliveryIndices, pickupCount, deliveryCount } = useMemo(() => {
    const orderEvents = formMethods.watch('orderEvents') || []
    const pickups: number[] = []
    const deliveries: number[] = []

    orderEvents.forEach((event: any, index: number) => {
      // Get eventType from the stored events or infer from form data
      const storedEvent = orderEventsWithIds[index]
      const eventType = storedEvent?.eventType || (event._eventType || 'PICKUP')
      
      if (eventType === 'PICKUP') {
        pickups.push(index)
      } else if (eventType === 'DELIVERY') {
        deliveries.push(index)
      }
    })
    
    return {
      pickupIndices: pickups,
      deliveryIndices: deliveries,
      pickupCount: pickups.length,
      deliveryCount: deliveries.length,
    }
  }, [formMethods, orderEventsWithIds])

  const hasPickup = pickupCount > 0
  const hasDelivery = deliveryCount > 0

  // Generate dynamic tabs (hide Customer & Rate for dispatch)
  const dynamicTabs = useMemo(() => {
    const baseTabs = [...verifyOrderTabs].filter((tab) => {
      if (isDispatch && (tab.id === 'customer' || tab.id === 'rate')) {
        return false
      }
      return true
    })

    const tabs = [...baseTabs]

    if (pickupCount > 0) {
      const pickupTabIndex = tabs.findIndex((tab) => tab.id === 'pickup')
      if (pickupTabIndex !== -1) {
        tabs.splice(pickupTabIndex, 1)
        pickupIndices.forEach((_, idx) => {
          const label = pickupCount === 1 ? 'Pickup' : `Pickup ${idx + 1}`
          tabs.splice(pickupTabIndex + idx, 0, { id: `pickup${idx + 1}`, label })
        })
      }
    }

    if (deliveryCount > 0) {
      const deliveryTabIndex = tabs.findIndex((tab) => tab.id === 'delivery')
      if (deliveryTabIndex !== -1) {
        tabs.splice(deliveryTabIndex, 1)
        deliveryIndices.forEach((_, idx) => {
          const label = deliveryCount === 1 ? 'Delivery' : `Delivery ${idx + 1}`
          tabs.splice(deliveryTabIndex + idx, 0, { id: `delivery${idx + 1}`, label })
        })
      }
    }

    return tabs
  }, [pickupCount, deliveryCount, pickupIndices, deliveryIndices, isDispatch])

  // Handle adding a new order event
  const handleAddEvent = (eventType: 'PICKUP' | 'DELIVERY') => {
    const currentOrderEvents = formMethods.getValues('orderEvents') || []
    const newEvent: any = {
      startTime: '',
      endTime: '',
      name: '',
      address: '',
      loadType: '',
      loadCount: '',
      temperatureValue: '',
      hazmat: '',
      pickupNumber: '',
      weightValue: '',
      notes: '',
      _orderEventId: undefined, // New event has no ID
      _eventType: eventType, // Store event type for tracking
    }
    
    // Add to form
    formMethods.setValue('orderEvents', [...currentOrderEvents, newEvent])
    
    // Add to tracking state
    const newEventWithId: OrderEvent & { _formIndex?: number } = {
      orderEventId: undefined,
      name: '',
      address: '',
      startTime: '',
      endTime: '',
      eventType: eventType,
      loadType: 'FTL',
      loadCount: 0,
      temperatureValue: 0,
      temperatureUnit: 'C',
      hazmat: '',
      weightValue: 0,
      weightUnit: '',
      pickupNumber: '',
      notes: '',
      status: 'NEW',
      _formIndex: currentOrderEvents.length,
    }
    const updatedEventsWithIds = [...orderEventsWithIds, newEventWithId]
    setOrderEventsWithIds(updatedEventsWithIds)
    
    // Calculate tab index based on updated events
    const pickupCount = updatedEventsWithIds.filter(e => e.eventType === 'PICKUP').length
    const deliveryCount = updatedEventsWithIds.filter(e => e.eventType === 'DELIVERY').length
    
    // Switch to the new tab
    if (eventType === 'PICKUP') {
      setTabsValue(2 + pickupCount - 1) // Switch to the new pickup tab (last pickup)
    } else {
      setTabsValue(2 + pickupCount + deliveryCount - 1) // Switch to the new delivery tab (last delivery)
    }
  }

  // Handle deleting an order event (only if it doesn't have an orderEventId)
  const handleDeleteEvent = (eventIndex: number) => {
    const currentOrderEvents = formMethods.getValues('orderEvents') || []
    const eventToDelete = currentOrderEvents[eventIndex]
    
    // Check if event has orderEventId - if it does, don't allow deletion
    if (eventToDelete?._orderEventId) {
      // Event already exists in database - cannot delete
      return
    }
    
    // Remove from form
    const updatedEvents = currentOrderEvents.filter((_: any, index: number) => index !== eventIndex)
    formMethods.setValue('orderEvents', updatedEvents)
    
    // Remove from tracking state
    const updatedEventsWithIds = orderEventsWithIds.filter((_, index) => index !== eventIndex)
    setOrderEventsWithIds(updatedEventsWithIds)
    
    // Adjust tab if needed
    if (tabsValue > eventIndex) {
      setTabsValue(Math.max(0, tabsValue - 1))
    }
  }

  const save = async (orderData: any) => {
    // Convert form data (strings) to backend format (numbers, dates, with required fields)
    const cleanedOrder: Order = {
      ...order,
      // Update order fields - convert strings to numbers
      customerName: orderData.customerName || order.customerName,
      loadNumber: orderData.loadNumber || order.loadNumber,
      baseRate: orderData.baseRate ? parseFloat(orderData.baseRate) : order.baseRate,
      detentionCharges: orderData.detentionCharges ? parseFloat(orderData.detentionCharges) : order.detentionCharges,
      layoverCharges: orderData.layoverCharges ? parseFloat(orderData.layoverCharges) : order.layoverCharges,
      fuelSurcharge: orderData.fuelSurcharge ? parseFloat(orderData.fuelSurcharge) : order.fuelSurcharge,
      otherCharges: orderData.otherCharges ? parseFloat(orderData.otherCharges) : order.otherCharges,
      currency: orderData.currency || order.currency,
      notes: orderData.notes || order.notes,
      // Map orderEvents from form data
      orderEvents: orderData.orderEvents?.map((formEvent: any, index: number) => {
        const storedEvent = orderEventsWithIds[index]
        const originalEvent = storedEvent || order.orderEvents?.[index]
        
        // Determine event type
        const eventType = storedEvent?.eventType || originalEvent?.eventType || formEvent._eventType || 'PICKUP'
        
        // Build event object - include orderEventId if it exists (for updates) or null for new events
        const eventData: any = {
          // Set orderEventId to the value if it exists (for existing events), or null for new events
          orderEventId: formEvent._orderEventId ? parseInt(formEvent._orderEventId, 10) : null,
          eventType: eventType,
          name: formEvent.name || originalEvent?.name || '',
          address: formEvent.address || originalEvent?.address || '',
          startTime: formEvent.startTime || originalEvent?.startTime || '',
          endTime: formEvent.endTime || originalEvent?.endTime || '',
          loadType: formEvent.loadType || originalEvent?.loadType || 'FTL',
          loadCount: formEvent.loadCount ? parseInt(formEvent.loadCount, 10) : (originalEvent?.loadCount || 0),
          temperatureValue: formEvent.temperatureValue ? parseFloat(formEvent.temperatureValue) : (originalEvent?.temperatureValue || 0),
          temperatureUnit: originalEvent?.temperatureUnit || 'C',
          weightValue: formEvent.weightValue ? parseFloat(formEvent.weightValue) : (originalEvent?.weightValue || 0),
          weightUnit: originalEvent?.weightUnit || '',
          hazmat: formEvent.hazmat || originalEvent?.hazmat || '',
          pickupNumber: formEvent.pickupNumber || originalEvent?.pickupNumber || '',
          notes: formEvent.notes || originalEvent?.notes || '',
          status: originalEvent?.status || 'NEW',
        }
        
        return eventData
      }).filter(Boolean) || order.orderEvents,
    }
    setUpdateValue(cleanedOrder)
    
    // Prepare company update data
    // Send email addresses and phone number from form data - backend will create/update the email/phone records
    if (company) {
      const updatedCompany = {
        id: company.id,
        // Customer name (UI) = company name (DB); send updated value to company table
        companyName: orderData.customerName ?? company.companyName ?? '',
        notificationEmail: orderData.notificationEmail || '',
        invoicePodEmail: orderData.billingEmail || '',
        accountPayableEmail: orderData.accountPayableEmail || '',
        phoneNumber: formatPhoneForSave(orderData.phone || ''),
      }
      setUpdateCompany(updatedCompany as typeof company)
    }
    
    setAlertOpen(true)
  }

  const handleCancel = () => {
    if (onClose) {
      onClose()
      return
    }
    navigate('/order')
  }

  return (
    <div style={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <BasicTabs
        tabs={dynamicTabs}
        value={tabsValue}
        handleChange={handleChange}
        onAddEvent={handleAddEvent}
        fillContent
      >
        <FormProvider {...formMethods}>
          <form
            onSubmit={formMethods.handleSubmit(save)}
            style={{
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              minHeight: 0,
            }}
          >
            <div
              style={{
                flex: '1 1 0%',
                minHeight: 0,
                overflow: 'auto',
              }}
            >
              <>
                {/* Hide Customer & Rate tabs for dispatch users */}
                {!isDispatch && (
                  <>
                    <TabPanel value={tabsValue} index={0}>
                      <LoadCustomerForm />
                    </TabPanel>
                    <TabPanel value={tabsValue} index={1}>
                      <LoadRateForm />
                    </TabPanel>
                  </>
                )}
                {/* Render pickup tabs */}
                {hasPickup && (
                  <>
                    {pickupIndices.map((eventIndex, idx) => {
                      const baseOffset = isDispatch ? 0 : 2
                      const tabIndex = baseOffset + idx
                      const currentOrderEvents = formMethods.watch('orderEvents') || []
                      const event = currentOrderEvents[eventIndex]
                      const canDelete = !event?._orderEventId // Can only delete if no orderEventId

                      return (
                        <TabPanel key={`pickup-${eventIndex}`} value={tabsValue} index={tabIndex}>
                          <div style={{ position: 'relative' }}>
                            {canDelete && (
                              <IconButton
                                onClick={() => handleDeleteEvent(eventIndex)}
                                sx={{
                                  position: 'absolute',
                                  top: 0,
                                  right: 0,
                                  zIndex: 10,
                                  color: 'error.main',
                                }}
                                size="small"
                              >
                                <CloseIcon />
                              </IconButton>
                            )}
                            <LoadPickupForm eventIndex={eventIndex} />
                          </div>
                        </TabPanel>
                      )
                    })}
                  </>
                )}
                {/* Render delivery tabs */}
                {hasDelivery && (
                  <>
                    {deliveryIndices.map((eventIndex, idx) => {
                      const baseOffset = isDispatch ? 0 : 2
                      const tabIndex = baseOffset + pickupCount + idx
                      const currentOrderEvents = formMethods.watch('orderEvents') || []
                      const event = currentOrderEvents[eventIndex]
                      const canDelete = !event?._orderEventId // Can only delete if no orderEventId

                      return (
                        <TabPanel key={`delivery-${eventIndex}`} value={tabsValue} index={tabIndex}>
                          <div style={{ position: 'relative' }}>
                            {canDelete && (
                              <IconButton
                                onClick={() => handleDeleteEvent(eventIndex)}
                                sx={{
                                  position: 'absolute',
                                  top: 0,
                                  right: 0,
                                  zIndex: 10,
                                  color: 'error.main',
                                }}
                                size="small"
                              >
                                <CloseIcon />
                              </IconButton>
                            )}
                            <LoadDeliveryForm eventIndex={eventIndex} />
                          </div>
                        </TabPanel>
                      )
                    })}
                  </>
                )}
                {/* Notes tab - always at the end */}
                <TabPanel
                  value={tabsValue}
                  index={(isDispatch ? 0 : 2) + pickupCount + deliveryCount}
                >
                  <LoadNotesForm />
                </TabPanel>
              </>
            </div>

            <ActionButtons
              firstBtnProps={{
                title: 'Save',
                bgcolor: 'primary.main',
                onClick: formMethods.handleSubmit(save),
              }}
              secondBtnProps={{
                type: 'button',
                title: 'Cancel',
                bgcolor: 'grey.500',
                onClick: handleCancel,
              }}
              containerSx={{ flexShrink: 0 }}
            />
          </form>
        </FormProvider>
      </BasicTabs>

      <UpdateOrderConfirmationDialog
        open={alertOpen}
        handleClose={() => setAlertOpen(false)}
        order={updateValue}
        company={updateCompany}
      />
    </div>
  )
}

export default VerifyOrderForm
